"use client"

import { useState, useEffect } from "react"
import { 
  Tag, Plus, Edit3, Trash2, X, Save, Search, ChevronRight, ChevronDown,
  AlertCircle, Layers
} from 'lucide-react'
import { authFetch } from "../utils/authFetch";

const GestionFamilias = ({ API_URL, onClose, token }) => {
  const [familias, setFamilias] = useState([]);
  const [subfamilias, setSubfamilias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedFamilias, setExpandedFamilias] = useState([]);
  
  const [showFamiliaModal, setShowFamiliaModal] = useState(false);
  const [showSubfamiliaModal, setShowSubfamiliaModal] = useState(false);
  const [editingFamilia, setEditingFamilia] = useState(null);
  const [editingSubfamilia, setEditingSubfamilia] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  const [familiaFormData, setFamiliaFormData] = useState({ nombre: "" });
  const [subfamiliaFormData, setSubfamiliaFormData] = useState({ nombre: "", familia_id: "" });

  const authHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  useEffect(() => {
    if (token) loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadFamilias(), loadSubfamilias()]);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadFamilias = async () => {
    try {
      const response = await authFetch(`${API_URL}/familias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) setFamilias(result.data);
    } catch (err) {
      console.error('Error loading familias:', err);
    }
  };

  const loadSubfamilias = async () => {
    try {
      const response = await authFetch(`${API_URL}/subfamilias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response) return;
      const result = await response.json();
      if (result.success) setSubfamilias(result.data);
    } catch (err) {
      console.error('Error loading subfamilias:', err);
    }
  };

  const toggleFamilia = (familiaId) => {
    setExpandedFamilias(prev =>
      prev.includes(familiaId) ? prev.filter(id => id !== familiaId) : [...prev, familiaId]
    );
  };

  const handleOpenFamiliaModal = (familia = null) => {
    if (familia) {
      setEditingFamilia(familia);
      setFamiliaFormData({ nombre: familia.nombre });
    } else {
      setEditingFamilia(null);
      setFamiliaFormData({ nombre: "" });
    }
    setShowFamiliaModal(true);
  };

  const handleOpenSubfamiliaModal = (subfamilia = null, familiaId = null) => {
    if (subfamilia) {
      setEditingSubfamilia(subfamilia);
      setSubfamiliaFormData({ nombre: subfamilia.nombre, familia_id: subfamilia.familia_id });
    } else {
      setEditingSubfamilia(null);
      setSubfamiliaFormData({ nombre: "", familia_id: familiaId || "" });
    }
    setShowSubfamiliaModal(true);
  };

  const handleCloseFamiliaModal = () => {
    setShowFamiliaModal(false);
    setEditingFamilia(null);
    setFamiliaFormData({ nombre: "" });
  };

  const handleCloseSubfamiliaModal = () => {
    setShowSubfamiliaModal(false);
    setEditingSubfamilia(null);
    setSubfamiliaFormData({ nombre: "", familia_id: "" });
  };

  const handleSaveFamilia = async () => {
    if (!familiaFormData.nombre.trim()) {
      alert('El nombre de la familia es requerido');
      return;
    }
    try {
      const url = editingFamilia ? `${API_URL}/familias/${editingFamilia.id}` : `${API_URL}/familias`;
      const method = editingFamilia ? 'PUT' : 'POST';
      const response = await authFetch(url, { method, headers: authHeaders, body: JSON.stringify(familiaFormData) });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(editingFamilia ? '✅ Familia actualizada' : '✅ Familia creada');
        await loadFamilias();
        handleCloseFamiliaModal();
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error saving familia:', err);
      alert('❌ Error al guardar familia');
    }
  };

  const handleSaveSubfamilia = async () => {
    if (!subfamiliaFormData.nombre.trim() || !subfamiliaFormData.familia_id) {
      alert('Complete todos los campos');
      return;
    }
    try {
      const url = editingSubfamilia ? `${API_URL}/subfamilias/${editingSubfamilia.id}` : `${API_URL}/subfamilias`;
      const method = editingSubfamilia ? 'PUT' : 'POST';
      const response = await authFetch(url, { method, headers: authHeaders, body: JSON.stringify(subfamiliaFormData) });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(editingSubfamilia ? '✅ Subfamilia actualizada' : '✅ Subfamilia creada');
        await loadSubfamilias();
        handleCloseSubfamiliaModal();
        if (!expandedFamilias.includes(parseInt(subfamiliaFormData.familia_id))) {
          toggleFamilia(parseInt(subfamiliaFormData.familia_id));
        }
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error saving subfamilia:', err);
      alert('❌ Error al guardar subfamilia');
    }
  };

  const handleDelete = async (item, type) => {
    try {
      const url = type === 'familia' ? `${API_URL}/familias/${item.id}` : `${API_URL}/subfamilias/${item.id}`;
      const response = await authFetch(url, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      if (!response) return;
      const result = await response.json();
      if (result.success) {
        alert(`✅ ${type === 'familia' ? 'Familia' : 'Subfamilia'} eliminada`);
        type === 'familia' ? await loadFamilias() : await loadSubfamilias();
        setShowDeleteConfirm(null);
      } else {
        alert('❌ Error: ' + result.error);
      }
    } catch (err) {
      console.error('Error deleting:', err);
      alert('❌ Error al eliminar');
    }
  };

  const getSubfamiliasByFamilia = (familiaId) => subfamilias.filter(s => s.familia_id === familiaId);

  const filteredFamilias = familias.filter(f =>
    f.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="gestion-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando familias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="gestion-container">
      <div className="gestion-header">
        <div className="header-left">
          <Tag size={24} className="header-icon" />
          <div>
            <h2>Gestión de Familias</h2>
            <p>{familias.length} familia{familias.length !== 1 ? 's' : ''} • {subfamilias.length} subfamilia{subfamilias.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-close" onClick={onClose}><X size={18} /></button>
        </div>
      </div>

      <div className="gestion-toolbar">
        <div className="search-box">
          <Search size={16} />
          <input type="text" placeholder="Buscar familias..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="toolbar-actions">
          <button className="btn-secondary" onClick={() => handleOpenSubfamiliaModal()}>
            <Plus size={16} /><span>Subfamilia</span>
          </button>
          <button className="btn-primary" onClick={() => handleOpenFamiliaModal()}>
            <Plus size={16} /><span>Familia</span>
          </button>
        </div>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th width="40"></th>
              <th>Nombre</th>
              <th>Subfamilias</th>
              <th>Productos</th>
              <th width="100">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredFamilias.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  <AlertCircle size={20} />
                  <span>No se encontraron familias</span>
                </td>
              </tr>
            ) : (
              filteredFamilias.map((familia) => {
                const isExpanded = expandedFamilias.includes(familia.id);
                const famSubfamilias = getSubfamiliasByFamilia(familia.id);
                return (
                  <>
                    <tr key={familia.id} className="familia-row">
                      <td>
                        <button className="expand-btn" onClick={() => toggleFamilia(familia.id)} disabled={famSubfamilias.length === 0}>
                          {famSubfamilias.length > 0 && (isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
                        </button>
                      </td>
                      <td className="font-medium">{familia.nombre}</td>
                      <td><span className="badge">{famSubfamilias.length}</span></td>
                      <td><span className="badge secondary">{familia.producto_count || 0}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-icon add" onClick={() => handleOpenSubfamiliaModal(null, familia.id)} title="Añadir subfamilia"><Plus size={14} /></button>
                          <button className="btn-icon edit" onClick={() => handleOpenFamiliaModal(familia)} title="Editar"><Edit3 size={14} /></button>
                          <button className="btn-icon delete" onClick={() => setShowDeleteConfirm({ item: familia, type: 'familia' })} title="Eliminar"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && famSubfamilias.map((subfamilia) => (
                      <tr key={`sub-${subfamilia.id}`} className="subfamilia-row">
                        <td></td>
                        <td>
                          <div className="subfamilia-name">
                            <Layers size={14} />
                            <span>{subfamilia.nombre}</span>
                          </div>
                        </td>
                        <td>-</td>
                        <td><span className="badge secondary">{subfamilia.producto_count || 0}</span></td>
                        <td>
                          <div className="action-buttons">
                            <button className="btn-icon edit" onClick={() => handleOpenSubfamiliaModal(subfamilia)} title="Editar"><Edit3 size={14} /></button>
                            <button className="btn-icon delete" onClick={() => setShowDeleteConfirm({ item: subfamilia, type: 'subfamilia' })} title="Eliminar"><Trash2 size={14} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showFamiliaModal && (
        <div className="modal-overlay" onClick={handleCloseFamiliaModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingFamilia ? 'Editar Familia' : 'Nueva Familia'}</h3>
              <button onClick={handleCloseFamiliaModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Nombre <span className="required">*</span></label>
                <input type="text" placeholder="Nombre de la familia" value={familiaFormData.nombre} onChange={(e) => setFamiliaFormData({ nombre: e.target.value })} className="input" autoFocus />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseFamiliaModal}>Cancelar</button>
              <button className="btn-primary" onClick={handleSaveFamilia} disabled={!familiaFormData.nombre.trim()}>
                <Save size={16} /><span>{editingFamilia ? 'Actualizar' : 'Crear'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubfamiliaModal && (
        <div className="modal-overlay" onClick={handleCloseSubfamiliaModal}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingSubfamilia ? 'Editar Subfamilia' : 'Nueva Subfamilia'}</h3>
              <button onClick={handleCloseSubfamiliaModal}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Familia <span className="required">*</span></label>
                <select value={subfamiliaFormData.familia_id} onChange={(e) => setSubfamiliaFormData({ ...subfamiliaFormData, familia_id: e.target.value })} className="input" disabled={!!editingSubfamilia}>
                  <option value="">Seleccionar familia</option>
                  {familias.map(f => <option key={f.id} value={f.id}>{f.nombre}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Nombre <span className="required">*</span></label>
                <input type="text" placeholder="Nombre de la subfamilia" value={subfamiliaFormData.nombre} onChange={(e) => setSubfamiliaFormData({ ...subfamiliaFormData, nombre: e.target.value })} className="input" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={handleCloseSubfamiliaModal}>Cancelar</button>
              <button className="btn-primary" onClick={handleSaveSubfamilia} disabled={!subfamiliaFormData.nombre.trim() || !subfamiliaFormData.familia_id}>
                <Save size={16} /><span>{editingSubfamilia ? 'Actualizar' : 'Crear'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
          <div className="modal-box confirm" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-icon"><AlertCircle size={48} /></div>
            <h3>¿Eliminar {showDeleteConfirm.type === 'familia' ? 'Familia' : 'Subfamilia'}?</h3>
            <p>Esta acción no se puede deshacer. Los productos asociados permanecerán pero sin {showDeleteConfirm.type === 'familia' ? 'familia' : 'subfamilia'} asignada.</p>
            <div className="confirm-actions">
              <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>Cancelar</button>
              <button className="btn-danger" onClick={() => handleDelete(showDeleteConfirm.item, showDeleteConfirm.type)}>
                <Trash2 size={16} /><span>Eliminar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        /* ... mismo CSS que el original ... */
        .gestion-container { display: flex; flex-direction: column; height: 100%; background: white; }
        .loading-state { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 400px; gap: 16px; }
        .spinner { width: 40px; height: 40px; border: 3px solid #E5E7EB; border-top: 3px solid #3B82F6; border-radius: 50%; animation: spin 0.8s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        .gestion-header { display: flex; justify-content: space-between; align-items: center; padding: 20px 24px; border-bottom: 1px solid #E5E7EB; }
        .header-left { display: flex; align-items: center; gap: 12px; }
        .header-icon { color: #10B981; }
        .gestion-header h2 { margin: 0; font-size: 20px; font-weight: 600; color: #1A1A1A; }
        .gestion-header p { margin: 2px 0 0 0; font-size: 13px; color: #64748B; }
        .header-actions { display: flex; gap: 8px; }
        .btn-close { padding: 6px; border: none; border-radius: 6px; background: #F1F5F9; color: #64748B; cursor: pointer; transition: all 0.2s; }
        .btn-close:hover { background: #E5E7EB; color: #EF4444; }
        .gestion-toolbar { display: flex; justify-content: space-between; align-items: center; padding: 16px 24px; border-bottom: 1px solid #E5E7EB; }
        .search-box { display: flex; align-items: center; gap: 8px; padding: 8px 12px; background: #F9FAFB; border: 1px solid #E5E7EB; border-radius: 6px; width: 300px; }
        .search-box svg { color: #94A3B8; flex-shrink: 0; }
        .search-box input { flex: 1; border: none; background: none; font-size: 13px; color: #1A1A1A; outline: none; }
        .search-box input::placeholder { color: #94A3B8; }
        .toolbar-actions { display: flex; gap: 8px; }
        .btn-primary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border: none; border-radius: 6px; background: #10B981; color: white; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-primary:hover { background: #059669; }
        .btn-primary:disabled { background: #CBD5E1; cursor: not-allowed; }
        .btn-secondary { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border: 1px solid #E5E7EB; border-radius: 6px; background: white; color: #64748B; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-secondary:hover { background: #F9FAFB; color: #10B981; }
        .table-container { flex: 1; overflow: auto; }
        .data-table { width: 100%; border-collapse: collapse; }
        .data-table thead { position: sticky; top: 0; background: #F9FAFB; z-index: 10; }
        .data-table th { padding: 12px 24px; text-align: left; font-size: 12px; font-weight: 600; color: #64748B; text-transform: uppercase; border-bottom: 1px solid #E5E7EB; }
        .data-table td { padding: 14px 24px; font-size: 13px; color: #1A1A1A; border-bottom: 1px solid #F1F5F9; }
        .familia-row:hover { background: #F9FAFB; }
        .subfamilia-row { background: #FAFAFA; }
        .subfamilia-row:hover { background: #F5F5F5; }
        .expand-btn { padding: 4px; border: none; background: transparent; color: #64748B; cursor: pointer; border-radius: 4px; transition: all 0.2s; }
        .expand-btn:hover:not(:disabled) { background: #F1F5F9; color: #10B981; }
        .expand-btn:disabled { cursor: default; }
        .subfamilia-name { display: flex; align-items: center; gap: 8px; padding-left: 20px; }
        .subfamilia-name svg { color: #10B981; }
        .font-medium { font-weight: 500; }
        .badge { display: inline-flex; align-items: center; justify-content: center; padding: 2px 8px; background: #DCFCE7; color: #10B981; font-size: 11px; font-weight: 600; border-radius: 10px; }
        .badge.secondary { background: #EFF6FF; color: #3B82F6; }
        .action-buttons { display: flex; gap: 4px; }
        .btn-icon { padding: 6px; border: none; border-radius: 4px; background: transparent; cursor: pointer; transition: all 0.2s; }
        .btn-icon.add { color: #10B981; }
        .btn-icon.add:hover { background: #DCFCE7; }
        .btn-icon.edit { color: #3B82F6; }
        .btn-icon.edit:hover { background: #EFF6FF; }
        .btn-icon.delete { color: #EF4444; }
        .btn-icon.delete:hover { background: #FEF2F2; }
        .empty-state { text-align: center; padding: 48px 24px !important; color: #94A3B8; }
        .empty-state svg { margin-bottom: 8px; }
        .empty-state span { display: block; font-size: 13px; }
        .modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal-box { background: white; border-radius: 8px; width: 100%; max-width: 480px; overflow: hidden; }
        .modal-box.confirm { max-width: 400px; text-align: center; }
        .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; border-bottom: 1px solid #E5E7EB; }
        .modal-header h3 { margin: 0; font-size: 17px; font-weight: 600; }
        .modal-header button { padding: 5px; border: none; border-radius: 4px; background: none; color: #64748B; cursor: pointer; transition: all 0.2s; }
        .modal-header button:hover { background: #F1F5F9; color: #EF4444; }
        .modal-body { padding: 20px; display: flex; flex-direction: column; gap: 14px; }
        .form-group { display: flex; flex-direction: column; gap: 5px; }
        .form-group label { font-size: 12px; font-weight: 500; color: #64748B; }
        .required { color: #EF4444; }
        .input { padding: 8px 10px; border: 1px solid #E5E7EB; border-radius: 6px; font-size: 13px; color: #1A1A1A; background: white; transition: all 0.2s; }
        .input:focus { outline: none; border-color: #10B981; box-shadow: 0 0 0 3px rgba(16,185,129,0.1); }
        .modal-footer { padding: 14px 20px; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 8px; }
        .confirm-icon { display: flex; justify-content: center; padding: 20px; color: #EF4444; }
        .modal-box.confirm h3 { margin: 0 0 8px 0; font-size: 18px; font-weight: 600; color: #1A1A1A; padding: 0 20px; }
        .modal-box.confirm p { margin: 0; padding: 0 20px 20px; font-size: 13px; color: #64748B; line-height: 1.5; }
        .confirm-actions { display: flex; gap: 8px; padding: 14px 20px; border-top: 1px solid #E5E7EB; justify-content: center; }
        .btn-danger { display: flex; align-items: center; gap: 6px; padding: 7px 14px; border: none; border-radius: 6px; background: #EF4444; color: white; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .btn-danger:hover { background: #DC2626; }
        @media (max-width: 768px) { .gestion-toolbar { flex-direction: column; gap: 12px; } .search-box { width: 100%; } }
      `}</style>
    </div>
  );
};

export default GestionFamilias;